import type { ActivationRequestData } from '@brighterly/lib-core-types/activation/interfaces'
import type { ChannelNotification } from '@brighterly/lib-core-types/lists/activation/enums'
import type { ShortDaysOfWeek } from '@brighterly/lib-core-types/options/enums'
import type {
  ActivationStore,
  DraftStepPersonalizationPayload,
  DraftStepSchedulePayload,
  DraftStepStartDatePayload,
} from './interfaces'
import type { TimeRange } from '@brighterly/lib-core-types/activation/enums'

import { StartDateType, Step, Expectations } from '@brighterly/lib-core-types/activation/enums'

import { isFullArray } from '@shared/lib/collections'

import { getActivationRequestData } from '../../lib/activation-request-data'

const isDraftScheduleStep = (state: ActivationStore): boolean => !!state.draftStepSchedule
const isDraftStartDateStep = (state: ActivationStore): boolean => !!state.draftStepStartDate
const isDraftPersonalizationStep = (state: ActivationStore): boolean => !!state.draftStepPersonalization
const isDraftTutorTypeStep = (state: ActivationStore): boolean => state.draftStepTutorType

export const getters = {
  isProcessing: (state: ActivationStore): boolean => state.processing,
  getTutorType: (state: ActivationStore): string | null => state.tutorType,
  getSchedule: (state: ActivationStore): Record<ShortDaysOfWeek, TimeRange[]> => state.schedule,
  getStartDate: (state: ActivationStore): string => state.startDate,
  getSwitcherStartDateType: (state: ActivationStore): StartDateType => state.switcherStartDateType,
  isDraftScheduleStep: (state: ActivationStore): boolean => isDraftScheduleStep(state),
  isDraftStartDateStep: (state: ActivationStore): boolean => isDraftStartDateStep(state),
  isDraftPersonalizationStep: (state: ActivationStore): boolean => isDraftPersonalizationStep(state),
  isDraftTutorTypeStep: (state: ActivationStore): boolean => isDraftTutorTypeStep(state),
  isDraftStep: (state: ActivationStore): boolean => isDraftScheduleStep(state) || isDraftStartDateStep(state) || isDraftPersonalizationStep(state) || isDraftTutorTypeStep(state),
  isValidSchedule: (state: ActivationStore): boolean => state.validSchedule,
  isValidStartDate: (state: ActivationStore): boolean => {
    const soon = (state.switcherStartDateType === StartDateType.Soon)
    return soon
      ? true
      : !!state.startDate
  },
  isValidScheduleStep: (state: ActivationStore): boolean => state.validSchedule,
  getRequestData: (state: ActivationStore): ActivationRequestData => getActivationRequestData(state),
  getDraftScheduleStep: (state: ActivationStore): DraftStepSchedulePayload | null => state.draftStepSchedule,
  isSwitcherStartDateTypeSoon: (state: ActivationStore): boolean => state.switcherStartDateType === StartDateType.Soon,
  isSwitcherStartDateTypeCalendar: (state: ActivationStore): boolean => state.switcherStartDateType === StartDateType.Calendar,
  getHomeStep: (state: ActivationStore): Step => state.homeStep,
  getDraftStartDateStep: (state: ActivationStore): DraftStepStartDatePayload | null => state.draftStepStartDate,
  isScreenRecording: (state: ActivationStore): boolean => state.screenRecording,
  getScreenRecording: (state: ActivationStore): boolean => state.screenRecording,
  getExpectations: (state: ActivationStore): Expectations[] => state.expectations,
  isTutorType: (state: ActivationStore): boolean => !!state.tutorType,
  isExpectations: (state: ActivationStore): boolean => isFullArray(state.expectations),
  isActivationReminders: (state: ActivationStore): boolean => isFullArray(state.activationReminders),
  getActivationReminders: (state: ActivationStore): ChannelNotification[] => state.activationReminders,
  getDraftPersonalizationStep: (state: ActivationStore): DraftStepPersonalizationPayload | null => state.draftStepPersonalization,
  getBookingAvailableDays: (state: ActivationStore): string[] | null => state.bookingAvailableDays,
  getBookingDate: (state: ActivationStore): string | null => state.bookingDate,
  getBookingTime: (state: ActivationStore): string | null => state.bookingTime,
  isBookingDate: (state: ActivationStore): boolean => !!state.bookingDate,
  isBookingTime: (state: ActivationStore): boolean => !!state.bookingTime,
  getTriggerBookingSelect: (state: ActivationStore): number => state.triggerBookingSelect,
  getAvailableTimes: (state: ActivationStore): any[] | null => state.availableTimes,
  isAvailableSlots: (state: ActivationStore): boolean => isFullArray(state.availableSlots),
  getAvailableSlots: (state: ActivationStore): any[] | null => state.availableSlots,
  getAvailableDays: (state: ActivationStore): string[] | null => state.availableDays,
  isModalBooked: (state: ActivationStore): ActivationStore['modalBooked'] => state.modalBooked,
  isModalPremiumTutorRetry: (state: ActivationStore): boolean => state.modalPremiumTutorRetry,
  isActivationCompleted: (state: ActivationStore): boolean => state.activationCompleted,
  getPreferences: (state: ActivationStore): string => state.preferences,
  isOptionsLoading: (state: ActivationStore): boolean => state.optionsLoading,
  getPremiumTutorUpsellOrder: (state: ActivationStore): ActivationStore['premiumTutorUpsellOrder'] => state.premiumTutorUpsellOrder,
  hasPremiumTutorUpsellOrder: (state: ActivationStore): boolean => !!state.premiumTutorUpsellOrder,
  getActivationOrderIdQuery: (state: ActivationStore): ActivationStore['activationOrderIdQuery'] => state.activationOrderIdQuery,
  hasActivationOrderIdQuery: (state: ActivationStore): boolean => {
    return typeof state.activationOrderIdQuery === 'number' && state.activationOrderIdQuery > 0
  },
}
